import React from "react";
import { createPortal } from "react-dom";
import Modal from "../../UI/Modal";
import { Button } from "../../UI/Button";
import { Dialog } from "../../UI/Dialog";

interface PluginDialogFrameProps {
  title: string;
  /** 수정 모드면 좌측 하단에 '연동 해제' 버튼이 생긴다 */
  isEditing: boolean;
  isBusy: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: () => void;
  onDelete: () => void;
  children: React.ReactNode;
}

/**
 * 플러그인 다이얼로그 공통 틀 (본문 + 저장/취소/연동 해제).
 *
 * 내 회사 정보 화면은 페이지 전체가 하나의 <form> 이라, 그 안에 다이얼로그를 그리면
 * 다이얼로그 입력란에서 Enter 를 누를 때 회사 정보 폼이 제출된다. 그래서 body 로 포털을 띄우고
 * 다이얼로그 자신의 <form> 으로 Enter 저장을 받는다.
 */
export const PluginDialogFrame = ({
  title,
  isEditing,
  isBusy,
  errorMessage,
  onClose,
  onSubmit,
  onDelete,
  children,
}: PluginDialogFrameProps) => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);

  return createPortal(
    <>
      <Modal
        setIsModalVisible={(visible) => {
          if (!visible && !isBusy) onClose();
        }}
        title={title}
      >
        <form
          className="w-[calc(100vw-20vw)] max-w-[31.875rem] text-start text-[14px]"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isBusy) onSubmit();
          }}
        >
          {children}

          {errorMessage && (
            <div className="mt-[0.75rem] p-[0.75rem] rounded-[8px] bg-[#EBF3FF] text-[#4285F4] text-[13px] border border-[#4285F4]/20">
              {errorMessage}
            </div>
          )}

          <div className="flex items-center justify-between mt-[1.5rem] gap-[0.5rem]">
            <div>
              {isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  size="md"
                  className="text-[#F24A4A] hover:text-[#D63939]"
                  disabled={isBusy}
                  onClick={() => setIsDeleteConfirmOpen(true)}
                >
                  연동 해제
                </Button>
              )}
            </div>
            <div className="flex gap-[0.5rem]">
              <Button
                type="button"
                variant="outline"
                size="md"
                disabled={isBusy}
                onClick={onClose}
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={isBusy}
                disabled={isBusy}
              >
                {isEditing ? "저장" : "등록"}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <Dialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => {
          setIsDeleteConfirmOpen(false);
          onDelete();
        }}
        title={`${title} 연동을 해제할까요?`}
        description="해제하면 쇼핑몰에서 이 서비스가 바로 사라집니다. 다시 등록하면 복구됩니다."
        confirmText="연동 해제"
        confirmVariant="danger"
      />
    </>,
    document.body,
  );
};
